import { useAuthFetch } from "@p0security/frontend-core";
import {
  ACCESS_TYPE_BLOCK_ID,
  REASON_BLOCK_ID,
  RESOURCE_SELECTOR_BLOCK_ID,
} from "@p0security/shared/integrations/notifiers/constants";
import type {
  GetRequestModalRequestBody,
  GetRequestModalResponseBody,
  GetSuggestionsRequestBody,
  GetSuggestionsResponseBody,
  WebAction,
  WebBlock,
  WebInputChoice,
  WebModalState,
} from "@p0security/shared/integrations/notifiers/web-requests/types";
import { compact, isEqual, pick } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useForm, useFormState, useWatch } from "react-hook-form";

import type { FetchOptionsArgs } from "./BlockRenderers";
import { filterDispatchBlocks, getBlockValues } from "./Utils";

export const useBlockForm = (options: {
  dispatchEndpoint: string;
  suggestionsEndpoint: string;
  filterBlocks?: (blocks: WebBlock[]) => WebBlock[];
  enabled?: boolean;
}) => {
  const {
    dispatchEndpoint,
    suggestionsEndpoint,
    filterBlocks,
    enabled = true,
  } = options;

  const [blocks, setBlocks] = useState<WebBlock[]>([]);
  const [actions, setActions] = useState<WebAction[]>([]);
  const [dispatching, setDispatching] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const authFetch = useAuthFetch(setError);

  const form = useForm({ mode: "onChange" });
  const { control, reset, getValues } = form;
  const { isDirty } = useFormState({ control });
  const watching = useWatch({ control });

  const setForm = useCallback(
    (newValues: WebModalState) => {
      reset(newValues, {
        keepDirty: true,
        keepDirtyValues: true,
      });
    },
    [reset]
  );

  /** Fetches the form for the given values
   *
   * `requestId`, when supplied, prefills the form from an existing request; it is
   * only honored on the initial dispatch, as thereafter the prefilled values are
   * carried by the form state.
   */
  const dispatch = useCallback(
    async (values: WebModalState, requestId?: string) => {
      try {
        setDispatching(true);
        const json: GetRequestModalRequestBody = { requestId, values };
        const result = await authFetch(dispatchEndpoint, {
          method: "POST",
          json,
        });
        if (!result) {
          setError("No response from server");
          return;
        }
        const data: GetRequestModalResponseBody = await result.json();
        if (!data.ok) {
          setError(data.error);
        } else {
          const finalBlocks = filterBlocks
            ? filterBlocks(data.blocks)
            : data.blocks;
          setForm(getBlockValues(finalBlocks));
          setBlocks(finalBlocks);
          setActions(data.actions);
        }
      } catch (e) {
        console.error("Request failed", e);
        setError("Error fetching options, check console for more information.");
      } finally {
        setDispatching(false);
      }
    },
    [authFetch, dispatchEndpoint, filterBlocks, setForm]
  );

  // Re-dispatch when any dispatch block value changes
  useEffect(() => {
    if (dispatching || !isDirty || !enabled) return;
    const previous = getBlockValues(blocks);

    if (
      previous[RESOURCE_SELECTOR_BLOCK_ID] !==
      watching[RESOURCE_SELECTOR_BLOCK_ID]
    ) {
      reset({
        [RESOURCE_SELECTOR_BLOCK_ID]: watching[RESOURCE_SELECTOR_BLOCK_ID],
      });
    } else if (
      watching[ACCESS_TYPE_BLOCK_ID] !== previous[ACCESS_TYPE_BLOCK_ID]
    ) {
      reset({
        [RESOURCE_SELECTOR_BLOCK_ID]: watching[RESOURCE_SELECTOR_BLOCK_ID],
        [ACCESS_TYPE_BLOCK_ID]: watching[ACCESS_TYPE_BLOCK_ID],
      });
    }

    let current = getValues();

    const blocksToCheck = blocks
      .filter(filterDispatchBlocks)
      .map((block) => block.id);

    const keysToCheck = Object.keys(current).filter((key) =>
      blocksToCheck.includes(key)
    );

    if (!keysToCheck.length) return;

    const changedKeys = compact(
      keysToCheck.filter((key) =>
        isEqual(current[key], previous[key]) ? undefined : key
      )
    );

    if (!changedKeys.length) return;

    if (changedKeys.includes(RESOURCE_SELECTOR_BLOCK_ID)) {
      current = pick(current, [RESOURCE_SELECTOR_BLOCK_ID]);
    } else if (changedKeys.includes(ACCESS_TYPE_BLOCK_ID)) {
      current = pick(current, [
        RESOURCE_SELECTOR_BLOCK_ID,
        ACCESS_TYPE_BLOCK_ID,
      ]);
    } else {
      // A dispatch block (e.g. an access argument) changed. Preserve the values
      // of every block at or before the changed block in display order, and
      // clear only downstream blocks, which may be regenerated based on the new
      // value. The reason renders after the access arguments but is free-form
      // text no block is derived from, so it is kept regardless of position.
      const orderedIds = blocks.map((b) => b.id);
      const changedIdx = Math.max(
        ...changedKeys.map((k) => orderedIds.indexOf(k)).filter((i) => i >= 0)
      );
      const keysToKeep = orderedIds.slice(0, changedIdx + 1);
      current = pick(current, [...keysToKeep, REASON_BLOCK_ID]);
      reset(current);
    }

    dispatch(current);
  }, [
    blocks,
    dispatching,
    isDirty,
    dispatch,
    watching,
    getValues,
    reset,
    enabled,
  ]);

  const handleFetchOptions = useCallback(
    async (args: FetchOptionsArgs): Promise<WebInputChoice[]> => {
      try {
        const json: GetSuggestionsRequestBody = {
          listerId: args.argId,
          query: args.query,
          values: watching,
        };
        const result = await authFetch(suggestionsEndpoint, {
          method: "POST",
          json,
        });
        if (!result) {
          setError("No response from server. Please try again.");
          return [];
        }
        const data: GetSuggestionsResponseBody = await result.json();
        if (data.ok) {
          return data.suggestions;
        } else {
          setError(data.error);
          return [];
        }
      } catch (e) {
        console.error("Request failed", e);
        setError("Error fetching options, check console for more information.");
        return [];
      }
    },
    [authFetch, suggestionsEndpoint, watching]
  );

  const clearError = useCallback(() => setError(undefined), []);

  const resetState = useCallback(() => {
    setBlocks([]);
    setActions([]);
    setError(undefined);
    form.reset();
  }, [form]);

  return {
    blocks,
    actions,
    form,
    watching,
    dispatching,
    error,
    clearError,
    setError,
    handleFetchOptions,
    dispatch,
    resetState,
  };
};
